/**
 * The checkout funnel, and what each step is called at each destination.
 *
 * `as const` plus the `AnalyticEventName` union is the point of having typed
 * this at all: a call site naming a step that does not exist stops compiling,
 * where before it read `AnalyticEvents[typo]['meta']` and threw at runtime, in
 * the buyer's browser, on the one page nobody watches the console on.
 */
export const AnalyticEvents = {
  /** The buyer has added a product to the cart. */
  add_to_cart: {
    gtm: 'gateme.add_to_cart',
    ga4: 'add_to_cart',
    meta: 'AddToCart', // Standard event
  },
  /** The buyer has pressed the Buy Now button. */
  begin_checkout: {
    gtm: 'gateme.begin_checkout',
    ga4: 'begin_checkout',
    meta: 'InitiateCheckout', // Standard event
  },
  /**
   * Express checkout only -- the purchaser had no account -- and fired once
   * they have successfully given their name and e-mail.
   */
  add_contact_info: {
    gtm: 'gateme.add_contact_info',
    ga4: 'add_contact_info',
    meta: 'CompleteRegistration', // Standard event
  },
  /** A payment method was clicked. */
  payment_method_selected: {
    gtm: 'gateme.payment_method_selected',
    ga4: 'payment_method_selected',
    // Custom event, not one of the standard ones:
    // https://developers.facebook.com/docs/meta-pixel/reference/#standard-events
    meta: 'PaymentMethodSelected',
  },
  /** Pay Now pressed; the buyer is on their way to the payment processor. */
  to_payment_processor: {
    gtm: 'gateme.to_payment_processor',
    ga4: 'add_payment_info',
    meta: 'AddPaymentInfo', // Standard event
  },
  /** The purchase completed and the buyer is looking at their tickets. */
  purchase: {
    gtm: 'gateme.purchase',
    ga4: 'purchase',
    meta: 'Purchase', // Standard event
  },
} as const;

export type AnalyticEventName = keyof typeof AnalyticEvents;
